import { getAuthHubUrl } from '../config';

// Auth Hub calls go through the global window.fetch. The Eatzy API fetch
// interceptor (AccountServiceInterceptors) has a same-origin guard that
// short-circuits any non-Eatzy-API origin, so these cross-origin requests
// pass through without token injection or EXPIRED_TOKEN logout side-effects.
const authHubFetch = async (path, options = {}) => {
  const url = `${getAuthHubUrl()}${path}`;
  const response = await window.fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  let data;
  try {
    data = await response.json();
  } catch (_) {
    data = {};
  }
  if (!response.ok) {
    const err = new Error(
      data?.message || data?.error || `Auth Hub request failed (${response.status})`
    );
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
};

const ROLE_SNAKE_TO_PASCAL = {
  restaurant_manager: 'RestaurantManager',
  local_area_manager: 'LocalAreaManager',
};

const REQUIRED_ROLES = ['RestaurantManager', 'LocalAreaManager'];

export const normaliseRoles = (rawRoles) => {
  if (!rawRoles) return {};
  if (Array.isArray(rawRoles)) {
    return rawRoles.reduce((acc, role) => {
      const normalisedKey = ROLE_SNAKE_TO_PASCAL[role] || role;
      return { ...acc, [normalisedKey]: true };
    }, {});
  }
  return Object.entries(rawRoles).reduce((acc, [key, value]) => {
    const normalisedKey = ROLE_SNAKE_TO_PASCAL[key] || key;
    return { ...acc, [normalisedKey]: value };
  }, {});
};

export const validateRoles = (roles) => {
  const hasAccess = REQUIRED_ROLES.some((role) => {
    const value = roles[role];
    return value !== undefined && value !== null && value !== false;
  });
  if (!hasAccess) {
    throw new Error(
      'Forbidden: you do not have Restaurant Manager or Local Area Manager access.'
    );
  }
};

export const authHubLogin = async (email, password) => {
  const data = await authHubFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  return data?.token || null;
};

// Normalise expiry values to Unix epoch seconds (integer).
// Auth Hub may return ISO strings or epoch numbers — OAuthentication.getToken()
// always uses (expires * 1000) for Date comparisons so we must ensure seconds.
// Returns undefined (not null) for missing/falsy values so that callers receive
// expiresIn = NaN, making _isTokenExpired return false (unknown expiry treated
// as valid). Backend-side expiry is then enforced via the 404 TOKEN_ERROR_MESSAGE
// path instead of crashing the login flow with an immediate 1970 expiry.
const normaliseExpiry = (value) => {
  if (!value) return undefined;
  if (typeof value === 'number') return value;
  return Math.floor(new Date(value).getTime() / 1000);
};

export const authHubGetEatzyToken = async (email, password) => {
  const data = await authHubFetch('/account/authorize', {
    method: 'POST',
    body: JSON.stringify({ username: email, password }),
  });

  const access = data.access
    ? { token: data.access.token, expires: normaliseExpiry(data.access.expires) }
    : { token: data.accessToken, expires: normaliseExpiry(data.accessExpires) };

  const refresh = data.refresh
    ? { token: data.refresh.token, expires: normaliseExpiry(data.refresh.expires) }
    : { token: data.refreshToken, expires: normaliseExpiry(data.refreshExpires) };

  const roles = normaliseRoles(data.roles);
  validateRoles(roles);

  return { access, refresh, roles };
};

export const authHubGetMe = async (sessionToken) => {
  const data = await authHubFetch('/auth/me', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
  });
  return data?.user || data;
};

// Per the Auth Hub shared password-reset contract: include `redirect_url` so
// the email link points back to *this* portal's /reset-password page. Without
// it the auth service falls back to the shared default host (Consumer/Kudoz)
// and the token becomes unusable for our users. The hub validates the URL
// against its trusted-origin list — staging/preview hosts must be whitelisted
// on the auth side or it silently falls back to the default.
export const authHubForgotPassword = async (email, redirectUrl) => {
  const body = redirectUrl ? { email, redirect_url: redirectUrl } : { email };
  return authHubFetch('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const authHubResetPassword = async (token, newPassword) => {
  return authHubFetch('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, new_password: newPassword }),
  });
};
